/**
 * Various fallbacks for when something goes wrong with push messages, e.g. if the browser is blocking them, or the server is unavailable.
 * We should only add fallbacks for issues that we don't know how to reproduce, and therefore cannot reliably fix.
 */

import { getCampaignRunsQueryOptions } from "@/api/campaign.api";
import { createLeadsQueryOptions } from "@/api/leads.api";
import { queryClient } from "@/utils/api";
import { store } from "@/utils/jotai";
import * as Sentry from "@sentry/react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { atom, useAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTimeout } from "usehooks-ts";
import { usePushMessageLeads } from "./push-message-leads";

const DEBUG = false;

const consoleDebug = DEBUG ? console.debug : () => {};

type CampaignId = number & {};

// ------------------------------------------------------------------------------------------------
// Fallback for when we we tried starting a campaign run, but we don't know if it's running or not.
// ------------------------------------------------------------------------------------------------

export const expectedRunsByCampaignIdAtom = atom(
  new Map<
    CampaignId,
    {
      startedTime: number;
    }
  >(),
);

export function markExpectedCampaignRun(campaignId: CampaignId) {
  store.set(expectedRunsByCampaignIdAtom, (prev) => {
    const newMap = new Map(prev);
    newMap.set(campaignId, {
      startedTime: Date.now(),
    });
    return newMap;
  });
}

export function useExpectedCampaignRunFallback(campaignId: CampaignId) {
  const [expectedRunsByCampaignId, setExpectedRunsByCampaignId] = useAtom(
    expectedRunsByCampaignIdAtom,
  );

  const expectedRun = useMemo(
    () => expectedRunsByCampaignId.get(campaignId),
    [expectedRunsByCampaignId, campaignId],
  );

  const campaignRunsQueryOptions = getCampaignRunsQueryOptions(campaignId);
  const campaignRunsQuery = useQuery(campaignRunsQueryOptions);

  const [shouldCheckForExpectedRun, setShouldCheckForExpectedRun] =
    useState(false);
  const checkCompletedRef = useRef(false);

  useEffect(() => {
    setShouldCheckForExpectedRun(false);
    checkCompletedRef.current = false;
  }, [expectedRun]);

  const timeoutMs = useMemo(() => {
    if (!expectedRun) return null;
    const timePassedSinceStarted = Date.now() - expectedRun.startedTime;
    return Math.max(0, 1000 - timePassedSinceStarted);
  }, [expectedRun]);

  useTimeout(() => {
    setShouldCheckForExpectedRun(true);
  }, timeoutMs);

  useEffect(() => {
    if (
      checkCompletedRef.current ||
      !shouldCheckForExpectedRun ||
      !expectedRun ||
      campaignRunsQuery.isFetching
    )
      return;

    const latestRun = campaignRunsQuery.data?.latestRun;

    consoleDebug("Checking for expected campaign run", {
      expectedRunStartDate: new Date(expectedRun.startedTime),
      latestRun,
    });

    if (latestRun) {
      const latestRunCompletedDate = latestRun.completed
        ? new Date(latestRun.completed)
        : null;

      // If the latest run is active, or completed after we expected the run to start, we have found the expected run and can stop checking for it.
      if (
        !latestRunCompletedDate ||
        latestRunCompletedDate.getTime() > expectedRun.startedTime
      ) {
        consoleDebug("Found expected campaign run", {
          expectedRunStartDate: new Date(expectedRun.startedTime),
          latestRunCompletedDate,
        });

        setExpectedRunsByCampaignId((prev) => {
          const prevExpectedRun = prev.get(campaignId);
          if (!prevExpectedRun || prevExpectedRun !== expectedRun) return prev;
          const newMap = new Map(prev);
          newMap.delete(campaignId);
          return newMap;
        });
        checkCompletedRef.current = true;
        return;
      }
    }

    consoleDebug(
      "Could not find expected campaign run, invalidating campaign runs query",
    );

    Sentry.captureException({
      title: "Expected campaign run fallback triggered",
      campaignId,
      campaignRunsQueryData: campaignRunsQuery.data,
    });

    queryClient.invalidateQueries({
      queryKey: campaignRunsQueryOptions.queryKey,
    });
    checkCompletedRef.current = true;
  }, [
    campaignId,
    campaignRunsQuery.data,
    campaignRunsQuery.isFetching,
    campaignRunsQueryOptions.queryKey,
    expectedRun,
    setExpectedRunsByCampaignId,
    shouldCheckForExpectedRun,
  ]);
}

// --------------------------------------------------------------------------------------------------
// Fallback for when we know a campaign run is active, but no leads are coming through push messages.
// --------------------------------------------------------------------------------------------------

export function useExpectedLeadsFallback({
  campaignId,
  params,
}: {
  campaignId: CampaignId;
  params: Parameters<typeof createLeadsQueryOptions>[0];
}) {
  const campaignRunsQueryOptions = getCampaignRunsQueryOptions(campaignId);
  const campaignRunsQuery = useQuery(campaignRunsQueryOptions);

  const latestRun = useMemo(
    () => campaignRunsQuery.data?.latestRun ?? null,
    [campaignRunsQuery.data],
  );

  const leadsQueryOptions = useMemo(
    () => createLeadsQueryOptions(params),
    [params],
  );
  const leadsQuery = useInfiniteQuery(leadsQueryOptions);

  const pushMessageLeads = usePushMessageLeads(leadsQueryOptions.queryKey);

  useEffect(() => {
    consoleDebug("expected leads fallback effect");

    if (campaignRunsQuery.isFetching) {
      consoleDebug(
        "Campaign runs query is still fetching, skipping leads fallback check",
      );
      return;
    }

    if (leadsQuery.isFetching) {
      consoleDebug(
        "Leads query is still fetching, skipping leads fallback check",
      );
      return;
    }

    if (!latestRun) {
      consoleDebug("No latest run, skipping leads fallback check");
      return;
    }

    if (latestRun.started === null) {
      consoleDebug("Latest run has not started, skipping leads fallback check");
      return;
    }

    if (latestRun.completed !== null) {
      consoleDebug("Latest run is completed, skipping leads fallback check");
      return;
    }

    const requestedLeads = latestRun.requestedLeads;
    const deliveredCount = latestRun.delivered ?? 0;
    const insufficientFundsCount = latestRun.insufficientFunds ?? 0;

    if (deliveredCount + insufficientFundsCount >= requestedLeads) {
      consoleDebug(
        "All leads have been delivered or marked as insufficient funds, skipping leads fallback check",
        {
          requestedLeads,
          deliveredCount,
          insufficientFundsCount,
        },
      );
      return;
    }

    const timeoutId = window.setTimeout(() => {
      consoleDebug("Leads timeout triggered!!!!");

      Sentry.captureException({
        title: "Expected leads fallback triggered",
        campaignId,
        runId: latestRun.id,
        requestedLeads,
        deliveredCount,
        insufficientFundsCount,
      });

      queryClient.invalidateQueries({
        queryKey: campaignRunsQueryOptions.queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: leadsQueryOptions.queryKey,
      });
    }, 10000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    campaignId,
    campaignRunsQueryOptions.queryKey,
    latestRun,
    leadsQueryOptions.queryKey,
    leadsQuery.data,
    pushMessageLeads,
    campaignRunsQuery.isFetching,
    leadsQuery.isFetching,
  ]);
}
